import { NextRouter, Router as RouterProps } from "next/router"
import NProgress from "nprogress"
import { head, isEmpty, reject, split } from "ramda"
import { Maybe, MerchantInfoQueryMerchantInfo } from "../graphql/generated-types"
import { defaultRoutes } from "../server/routes"
import { persistUrlQueries } from "./url"
import { AppSectionEnum } from "../components/types"

export const getRouterQuery = (router: RouterProps | NextRouter, query: string): Maybe<string> => {
    const queryData = router?.query?.[query]
    // console.log(router.query)
    if (Array.isArray(queryData)) {
        return head(queryData) ?? null
    } else {
        return queryData ?? null
    }
}

export const getActiveAppSection = (router: RouterProps | NextRouter): AppSectionEnum => {
    const tab = getRouterQuery(router, "tab") as Maybe<AppSectionEnum>
    return !tab || tab === AppSectionEnum.ProductDetail ? AppSectionEnum.Menu : tab
}

export const shouldRenderLocationPickerScreen = (merchantInfo: MerchantInfoQueryMerchantInfo) => {
    if ((merchantInfo.mibLocations || []).length > 1) {
        return true
    }
    return false
}

export const shouldRedirectToLocationUrl = (
    merchantInfo: MerchantInfoQueryMerchantInfo,
    currentAbsoluteUrl: string
) => {
    // Strip parameters and trailing slash from url
    const absoluteUrl = currentAbsoluteUrl.replace(/\/$/, "").split("?")[0]
    if (
        (merchantInfo.mibLocations || []).length === 1 &&
        !absoluteUrl.includes("localhost:") &&
        !absoluteUrl.includes("butter:") &&
        head(merchantInfo.mibLocations)?.url !== getBaseAbsoluteUrl(absoluteUrl)
    ) {
        return true
    }
    return false
}

export const parsePath = (path: string) => reject(isEmpty, split("/", path))

export const addSlugsToPath = (router: RouterProps | NextRouter, path: string): Maybe<string> => {
    const url = `${getBasePath()}${!path || path.charAt(0) !== "/" ? "/" : ""}${path}`
    if (url !== "/" && url.slice(-1) === "/") {
        return url.replace(/\/$/, "")
    }
    return url
}

export const getBasePath = (p?: string) => {
    const path = p ?? window.location.pathname
    if (path === "/") {
        return ""
    }
    const regex = new RegExp(`/(${Object.values(AppSectionEnum).join("|")}).*`)
    return path.replace(regex, "")
}

export const getBaseAbsoluteUrl = (p?: string) => {
    const path = p ?? window.location.href
    if (path === "/") {
        return ""
    }
    const regex = new RegExp(`/(${Object.values(AppSectionEnum).join("|")}).*`)
    return path.replace(regex, "")
}

export const trimSlugsFromPath = (p?: string): string => {
    const path = p ?? window.location.pathname
    const basePath = getBasePath(p)
    return path.replace(basePath, "")
}

export const getLinkWithSlugs = (router: RouterProps | NextRouter, path: string) =>
    persistUrlQueries(router, addSlugsToPath(router, path))

export const navigateToPath = (
    router: RouterProps | NextRouter,
    path: string,
    options: { replace?: boolean; shallow?: boolean } = { shallow: false }
) => {
    const Router = importRouter()
    const url = persistUrlQueries(router, addSlugsToPath(router, path))

    Router[options.replace ? "replaceRoute" : "pushRoute"](url, {}, { shallow: options.shallow })
}

export const navigateToPathWithReload = (router: RouterProps | NextRouter, path: string) => {
    window.location.href = persistUrlQueries(router, addSlugsToPath(router, path))
}

export const configureRouterProgressBar = () => {
    const Router = importRouter()
    Router.events.on("routeChangeStart", () => {
        NProgress.configure({ showSpinner: false })
        NProgress.start()
    })
    Router.events.on("routeChangeComplete", () => {
        return NProgress.done()
    })
    Router.events.on("routeChangeError", () => {
        return NProgress.done()
    })
}

export const importRouter = () => defaultRoutes.Router
export const importLink = () => defaultRoutes.Link
